import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: #6c757d;
  margin: 0;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid #dee2e6;
  color: #495057;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DataSummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const SummaryTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px 0;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: #6c757d;
`;

const StatValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
`;

const AnalysisSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const AnalysisTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px 0;
`;

const PlaceholderText = styled.p`
  font-size: 16px;
  color: #6c757d;
  text-align: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin: 20px 0;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  margin-bottom: 24px;
`;

// Analytics Helper Functions
const mapQuestionsAndResponses = (pulseSurveyData, debriefData, core_data) => {
  if (!pulseSurveyData || !debriefData) {
    return [];
  }

  const questionComparisons = [];

  // Get pulse questions and responses
  const pulseQuestions = pulseSurveyData.questions || [];
  const pulseResponses = pulseSurveyData.responses || [];
  
  // Get debrief responses
  const debriefResponses = debriefData.allResponses || [];

  console.log("Mapping Questions Debug:", {
    pulseQuestionsCount: pulseQuestions.length,
    pulseResponsesCount: pulseResponses.length,
    debriefResponsesCount: debriefResponses.length,
    samplePulseQuestion: pulseQuestions[0],
    samplePulseResponse: pulseResponses[0]?.task_response?.task_list?.[0],
    sampleDebriefResponse: debriefResponses[0]?.response?.questions?.slice(0, 3)
  });

  // Process each pulse question
  pulseQuestions.forEach((pulseQuestion, questionIndex) => {
    if (!pulseQuestion.meta_data) return;

    const { dimension, factor, q_id } = pulseQuestion.meta_data;
    
    console.log(`Processing question ${questionIndex}:`, { 
      dimension, 
      factor, 
      q_id, 
      questionText: pulseQuestion.question.substring(0, 50) 
    });
    
    // Calculate pulse aggregate score - user's method
    const pulseScores = [];
    pulseResponses.forEach(response => {
      const taskList = response.task_response?.task_list || [];
      // Try to find the response by index first (matching question order)
      if (taskList[questionIndex] && typeof taskList[questionIndex].response === 'number') {
        pulseScores.push(taskList[questionIndex].response);
      }
    });

    // Calculate debrief aggregate score - user's exact method
    const debriefScores = [];
    debriefResponses.forEach(debriefResponse => {
      const questions = debriefResponse.response?.questions || [];
      
      // User's exact matching logic
      const matchingQuestion = questions.find(f => 
        f.id === dimension && f.factor === factor && f.q === q_id
      );
      
      if (matchingQuestion && typeof matchingQuestion.response === 'number') {
        debriefScores.push(matchingQuestion.response);
      }
    });

    // Calculate averages
    const pulseAverage = pulseScores.length > 0 
      ? pulseScores.reduce((sum, score) => sum + score, 0) / pulseScores.length 
      : null;
    
    const debriefAverage = debriefScores.length > 0 
      ? debriefScores.reduce((sum, score) => sum + score, 0) / debriefScores.length 
      : null;

    console.log(`Question ${dimension}_${factor}_${q_id} results:`, {
      questionText: pulseQuestion.question.substring(0, 50) + "...",
      pulseScores: pulseScores,
      debriefScores: debriefScores,
      pulseAverage,
      debriefAverage
    });

    // Only include if we have data from at least one source
    if (pulseAverage !== null || debriefAverage !== null) {
      // Get dimension and factor names from core_data if available
      let dimensionName = `Dimension ${dimension}`;
      let factorName = `Factor ${factor}`;
      
      if (core_data?.questions?.dimensions) {
        const coreDimension = core_data.questions.dimensions.find(d => d.id === dimension + 1);
        if (coreDimension) {
          dimensionName = coreDimension.title;
          const coreFactor = coreDimension.factors.find(f => f.id === factor + 1);
          if (coreFactor) {
            factorName = coreFactor.title;
          }
        }
      }

      questionComparisons.push({
        questionId: `${dimension}_${factor}_${q_id}`,
        questionText: pulseQuestion.question,
        questionTextFrench: pulseQuestion.questionFrench,
        dimension: dimensionName,
        factor: factorName,
        dimensionId: dimension,
        factorId: factor,
        qId: q_id,
        pulseAverage: pulseAverage ? Math.round(pulseAverage * 10) / 10 : null,
        debriefAverage: debriefAverage ? Math.round(debriefAverage * 10) / 10 : null,
        pulseCount: pulseScores.length,
        debriefCount: debriefScores.length,
        difference: (pulseAverage !== null && debriefAverage !== null) 
          ? Math.round((pulseAverage - debriefAverage) * 10) / 10 
          : null
      });
    }
  });

  // Sort by dimension, then factor, then q_id
  questionComparisons.sort((a, b) => {
    if (a.dimensionId !== b.dimensionId) return a.dimensionId - b.dimensionId;
    if (a.factorId !== b.factorId) return a.factorId - b.factorId;
    return a.qId - b.qId;
  });

  console.log("Final question comparisons:", questionComparisons);
  
  return questionComparisons;
};

// Styled Components for Analytics
const AnalyticsContainer = styled.div`
  margin-top: 24px;
`;

const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AnalyticsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  max-width: 800px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryMetric = styled.div`
  background: white;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  text-align: left;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const QuestionsTable = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 100px 100px 100px;
  background-color: #f8f9fa;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
  
  @media (max-width: 1200px) {
    grid-template-columns: 2fr 80px 80px 80px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 100px 100px 100px;
  padding: 16px 12px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: 2fr 80px 80px 80px;
  }
`;

const QuestionInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuestionText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const QuestionMeta = styled.div`
  font-size: 11px;
  color: #6c757d;
`;

const ScoreCell = styled.div`
  text-align: center;
  font-weight: 500;
  color: ${props => props.color || '#495057'};
`;

const DifferenceCell = styled.div`
  text-align: center;
  font-weight: 600;
  color: ${props => {
    if (props.value > 0) return '#28a745';
    if (props.value < 0) return '#dc3545';
    return '#6c757d';
  }};
`;

const FilterSection = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  background: white;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #495057;
`;

const ComparisonAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [pulseSurveyData, setPulseSurveyData] = useState(null);
  const [debriefData, setDebriefData] = useState(null);
  const [selectedPulseSurvey, setSelectedPulseSurvey] = useState(null);
  const [selectedDebrief, setSelectedDebrief] = useState(null);

  // Extract data from navigation state
  useEffect(() => {
    if (location.state) {
      const { 
        pulseSurveyData: pulseData, 
        debriefData: debrief, 
        selectedPulseSurvey: pulse,
        selectedDebrief: debriefSelection
      } = location.state;
      
      setPulseSurveyData(pulseData);
      setDebriefData(debrief);
      setSelectedPulseSurvey(pulse);
      setSelectedDebrief(debriefSelection);
    }
  }, [location.state]);

  if (!pulseSurveyData || !debriefData) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>
            No data available for comparison. Please select both a pulse survey and debrief report.
          </ErrorMessage>
          <BackButton onClick={() => navigate(-1)}>
            ← Back to Selection
          </BackButton>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <PageHeader>
          <PageTitle>📊 Data Comparison Analysis</PageTitle>
          <PageSubtitle>Comparing pulse survey responses with survey findings</PageSubtitle>
          <BackButton onClick={() => navigate(-1)}>
            ← Back to Selection
          </BackButton>
        </PageHeader>

        {/* Analysis Section */}
        <AnalysisSection>
          <AnalysisTitle>🔍 Question-by-Question Comparison</AnalysisTitle>
          
          {(() => {
            const questionComparisons = mapQuestionsAndResponses(pulseSurveyData, debriefData, pulseSurveyData?.surveyStructure?.questionStructure);
            
            if (!questionComparisons.length) {
              return (
                <PlaceholderText>
                  No matching questions found between pulse survey and survey data.
                  <br /><br />
                  <strong>Debug Information:</strong>
                  <br />
                  • Pulse Questions: {pulseSurveyData?.questions?.length || 0}
                  <br />
                  • Pulse Responses: {pulseSurveyData?.responses?.length || 0}
                  <br />
                  • Survey Responses: {debriefData?.allResponses?.length || 0}
                  <br />
                  • Core Data Dimensions: {pulseSurveyData?.surveyStructure?.questionStructure?.dimensions?.length || 0}
                  <br /><br />
                  This may occur if the surveys use different question structures or if there are no overlapping responses.
                  Check the browser console for detailed debugging information.
                </PlaceholderText>
              );
            }

            // Calculate summary metrics
            const totalQuestions = questionComparisons.length;
            const questionsWithBothScores = questionComparisons.filter(q => q.pulseAverage !== null && q.debriefAverage !== null).length;
            const avgPulseScore = questionComparisons
              .filter(q => q.pulseAverage !== null)
              .reduce((sum, q, _, arr) => sum + q.pulseAverage / arr.length, 0);
            const avgDebriefScore = questionComparisons
              .filter(q => q.debriefAverage !== null)
              .reduce((sum, q, _, arr) => sum + q.debriefAverage / arr.length, 0);

            return (
              <AnalyticsContainer>
                {/* Summary Metrics - Pulse, Survey, and Change */}
                <AnalyticsSummary>
                  <SummaryMetric>
                    <MetricValue>{avgPulseScore.toFixed(1)}</MetricValue>
                    <MetricLabel>Average Pulse Score</MetricLabel>
                  </SummaryMetric>
                  <SummaryMetric>
                    <MetricValue>{avgDebriefScore.toFixed(1)}</MetricValue>
                    <MetricLabel>Average Survey Score</MetricLabel>
                  </SummaryMetric>
                  <SummaryMetric>
                    <MetricValue style={{ color: (avgPulseScore - avgDebriefScore) >= 0 ? '#28a745' : '#dc3545' }}>
                      {(avgPulseScore - avgDebriefScore) >= 0 ? '+' : ''}{(avgPulseScore - avgDebriefScore).toFixed(1)}
                    </MetricValue>
                    <MetricLabel>Average Change</MetricLabel>
                  </SummaryMetric>
                </AnalyticsSummary>

                {/* Questions Comparison Table */}
                <QuestionsTable>
                  <TableHeader>
                    <div>Question</div>
                    <div>Pulse Score</div>
                    <div>Survey Score</div>
                    <div>Difference</div>
                  </TableHeader>
                  
                  {questionComparisons.map((question, index) => (
                    <TableRow key={question.questionId}>
                      <QuestionInfo>
                        <QuestionText>{question.questionText}</QuestionText>
                      </QuestionInfo>
                      
                      <ScoreCell color={question.pulseAverage ? '#2c3e50' : '#6c757d'}>
                        {question.pulseAverage !== null ? question.pulseAverage : '—'}
                      </ScoreCell>
                      
                      <ScoreCell color={question.debriefAverage ? '#2c3e50' : '#6c757d'}>
                        {question.debriefAverage !== null ? question.debriefAverage : '—'}
                      </ScoreCell>
                      
                      <DifferenceCell value={question.difference}>
                        {question.difference !== null 
                          ? `${question.difference > 0 ? '+' : ''}${question.difference}`
                          : '—'
                        }
                      </DifferenceCell>
                    </TableRow>
                  ))}
                </QuestionsTable>

                <div style={{ marginTop: '16px', fontSize: '12px', color: '#6c757d' }}>
                  <strong>Note:</strong> Scores are averaged across all responses. 
                  Positive differences indicate pulse scores are higher than survey scores.
                </div>
              </AnalyticsContainer>
            );
          })()}
        </AnalysisSection>
      </Container>
    </PageContainer>
  );
};

export default ComparisonAnalysis; 